import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormeoRenderer } from 'formeo';
import intakeFormService from '@/services/intake-form/intakeFormService';
import 'formeo/dist/formeo.min.css';
// import { PublicHeader } from '@/layouts/main/header-public';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import Select from 'react-select';
import { createRoot } from 'react-dom/client';
import { getGlobalSelectClass, sortData } from '@/utils';
import { stateCityMap } from '@/utils';
import { createPortal } from 'react-dom';
import { injectCheckboxOtherOptions, applyCheckboxRequiredAttributes } from './checkboxOther';
import { applyInputKinds, enhanceSelectPlaceholders, getInputKinds } from './fieldInputs';
import {
    ALLOWED_FILE_EXTENSIONS,
    isAllowedFileType,
    getRequiredFieldsFromSchema,
    buildIntakeSubmission,
} from './intakeFormSubmit';
import { showFieldProblems } from './fieldProblems';
import useBodyClasses from '@/hooks/useBodyClasses';
import PropTypes from 'prop-types';
import { embedHeightMessage } from './embedCode';

// Helpers
const HIDDEN_RENDER_FIELDS = new Set(["matter.name"]);
const ACCEPT_FILE_TYPES = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');

// True inside an iframe, i.e. the "Copy Embed Code" snippet on a firm's site.
// The form then drops our page background and card so it blends into the site.
const isEmbedded = () => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};

const formatFileSize = (bytes = 0) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
};

const buildSelectedFileSummary = (files = []) => {
    if (!files.length) return '';
    return files.map((f) => `${f.name} (${formatFileSize(f.size)})`).join(', ');
};

const fileSelectionMap = new WeakMap();

const setInputFiles = (input, files) => {
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    input.files = dt.files;
    fileSelectionMap.set(input, files);
};

const getInputFiles = (input) => fileSelectionMap.get(input) || Array.from(input.files || []);

const renderFileList = (listEl, input) => {
    const files = getInputFiles(input);
    listEl.innerHTML = '';
    if (!files.length) return;

    const header = document.createElement('div');
    header.className = 'w-full flex items-center justify-between mb-1';

    const title = document.createElement('span');
    title.className = 'text-xs text-gray-500';
    title.textContent = 'Selected files';

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'text-xs text-red-600 hover:underline';
    clearBtn.textContent = 'Remove all';
    clearBtn.dataset.removeAll = 'true';

    header.appendChild(title);
    header.appendChild(clearBtn);
    listEl.appendChild(header);

    files.forEach((file, idx) => {
        const row = document.createElement('div');
        row.className = 'sm:w-[32%] w-full flex items-center justify-between gap-2 border border-primary rounded-md p-2 bg-primary-light flex justify-between';

        const name = document.createElement('span');
        name.className = 'truncate truncate text-gray-700';
        name.textContent = `${file.name} (${formatFileSize(file.size)})`;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'intake-form text-danger text-xs underline';
        btn.textContent = 'Remove';
        btn.dataset.removeIndex = String(idx);

        row.appendChild(name);
        row.appendChild(btn);
        listEl.appendChild(row);
    });
};

const stripFieldsFromSchema = (schema, hiddenNames) => {
    if (!schema?.fields || !hiddenNames?.size) return schema;

    const fieldIdsToRemove = new Set();

    Object.entries(schema.fields).forEach(([fieldId, field]) => {
        const name =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;

        if (name && hiddenNames.has(name)) {
            fieldIdsToRemove.add(fieldId);
        }
    });

    if (!fieldIdsToRemove.size) return schema;

    fieldIdsToRemove.forEach((fieldId) => {
        delete schema.fields[fieldId];
    });

    const columnIdsToRemove = new Set();
    if (schema.columns) {
        Object.entries(schema.columns).forEach(([columnId, column]) => {
            if (!Array.isArray(column.children)) return;
            column.children = column.children.filter((id) => !fieldIdsToRemove.has(id));
            if (!column.children.length) {
                columnIdsToRemove.add(columnId);
            }
        });

        columnIdsToRemove.forEach((columnId) => {
            delete schema.columns[columnId];
        });
    }

    const rowIdsToRemove = new Set();
    if (schema.rows) {
        Object.entries(schema.rows).forEach(([rowId, row]) => {
            if (!Array.isArray(row.children)) return;
            row.children = row.children.filter((id) => !columnIdsToRemove.has(id));
            if (!row.children.length) {
                rowIdsToRemove.add(rowId);
            }
        });

        rowIdsToRemove.forEach((rowId) => {
            delete schema.rows[rowId];
        });
    }

    if (schema.stages) {
        Object.values(schema.stages).forEach((stage) => {
            if (!Array.isArray(stage.children)) return;
            stage.children = stage.children.filter((id) => !rowIdsToRemove.has(id));
        });
    }

    return schema;
};

const buildFormData = (container) => {
    const rawData = {};
    const formData = new FormData();
    const elements = container.querySelectorAll('input, select, textarea');

    elements.forEach((el) => {
        if (!el.name) return;

        if (el.type === 'checkbox') {
            rawData[el.name] = el.checked;
            formData.append(el.name, String(el.checked));
        } else if (el.type === 'radio') {
            if (el.checked) {
                rawData[el.name] = el.value;
                formData.append(el.name, el.value);
            }
        } else if (el.type === 'file') {
            if (el.files && el.files.length > 0) {
                Array.from(el.files).forEach((file) => {
                    formData.append(el.name, file);
                });
            }
        } else if (el.tagName === 'SELECT' && el.multiple) {
            const values = Array.from(el.selectedOptions).map(o => o.value);
            rawData[el.name] = values;
            values.forEach(v => formData.append(`${el.name}[]`, v));
        } else {
            rawData[el.name] = el.value;
            formData.append(el.name, el.value);
        }
    });

    return { rawData, formData };
};

// Render react-select for multi select fields
const enhanceMultiSelects = (container) => {
    const selects = container.querySelectorAll('select[multiple]');

    selects.forEach((select) => {
        if (select.dataset.enhanced === 'true') return;
        select.dataset.enhanced = 'true';

        const options = Array.from(select.options).map(opt => ({
            label: opt.text,
            value: opt.value,
        }));

        const mount = document.createElement('div');
        mount.className = 'react-select-wrapper';

        select.classList.add('hidden-native-select');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');

        select.parentNode.insertBefore(mount, select.nextSibling);

        // The rendered <select> can come up with its first option selected even
        // though nobody chose it, and it would be submitted. Start from the
        // options marked as defaults only.
        Array.from(select.options).forEach((opt) => {
            opt.selected = opt.defaultSelected;
        });
        const initialValue = Array.from(select.selectedOptions).map(o => ({
            label: o.text,
            value: o.value,
        }));

        const root = createRoot(mount);

        root.render(
            <Select
                classNames={getGlobalSelectClass()}
                isMulti
                options={options}
                defaultValue={initialValue}
                className='input'
                classNamePrefix="react-select"
                placeholder={select.getAttribute('placeholder') || 'Select options...'}
                onChange={(selected) => {
                    const values = (selected || []).map(s => s.value);

                    // Sync values back to native select
                    Array.from(select.options).forEach(opt => {
                        opt.selected = values.includes(opt.value);
                    });

                    select.dispatchEvent(new Event('change', { bubbles: true }));
                }}
            />
        );
    });
};

const buildFieldLabelMapFromSchema = (schema) => {
    const map = {};
    const fields = Array.isArray(schema?.fields) ? schema.fields : Object.values(schema?.fields || {});

    fields.forEach((field) => {
        const name =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;

        if (!name) return;

        const label =
            field?.config?.label ||
            field?.attrs?.label ||
            field?.attrs?.placeholder ||
            name;

        map[name] = label;
    });

    return map;
};

const attachFieldLabelsToInputs = (container, labelMap) => {
    if (!container || !labelMap) return;

    Object.entries(labelMap).forEach(([name, label]) => {
        const els = container.querySelectorAll(`[name="${CSS.escape(name)}"]`);
        els.forEach((el) => {
            el.dataset.fieldLabel = label;
        });
    });
};

const enhanceRenderedSingleCheckboxPreview = (container) => {
    if (!container) return;

    const otherRows = container.querySelectorAll(".formeo-other-option[data-other-for]");

    otherRows.forEach((otherRow) => {
        const parent = otherRow.parentElement;
        if (!parent) return;

        const directCheckboxes = Array.from(
            parent.querySelectorAll(':scope > input[type="checkbox"]')
        );

        if (directCheckboxes.length !== 1) return;

        const checkbox = directCheckboxes[0];
        const label =
            parent.querySelector(`:scope > label[for="${CSS.escape(checkbox.id)}"]`) ||
            checkbox.nextElementSibling;

        if (!(label instanceof HTMLLabelElement)) return;

        let pairWrap = parent.querySelector('[data-single-checkbox-pair="true"]');
        if (!pairWrap) {
            pairWrap = document.createElement("div");
            pairWrap.dataset.singleCheckboxPair = "true";
            pairWrap.style.setProperty("display", "inline-flex");
            pairWrap.style.setProperty("align-items", "center");
            pairWrap.style.setProperty("gap", "0.5rem");
            pairWrap.style.setProperty("flex-wrap", "wrap");
            parent.insertBefore(pairWrap, checkbox);
        }

        if (checkbox.parentElement !== pairWrap) {
            pairWrap.appendChild(checkbox);
        }

        if (label.parentElement !== pairWrap) {
            pairWrap.appendChild(label);
        }

        parent.style.setProperty("display", "flex");
        parent.style.setProperty("flex-direction", "column");
        parent.style.setProperty("align-items", "flex-start");
        parent.style.setProperty("gap", "0.5rem");

        otherRow.style.setProperty("margin-top", "0");
    });
};

const getFormeoControl = (input) => {
    return input?.closest('.formeo-control') || input?.parentElement;
};

const CityStatePortaledSelect = ({
    stateInput,
    cityInput,
    stateMount,
    cityMount,
}) => {
    const [state, setState] = useState(stateInput?.value || '');
    const [city, setCity] = useState(cityInput?.value || '');

    // Options for state (custom dropdown)
    const stateOptions = sortData(
        Object.keys(stateCityMap).map((state) => ({
            label: state,
            value: state,
        })),
        { mode: "alpha", key: "label" }
    );

    // Sync input values with the native inputs
    const sync = (input, value) => {
        if (!input) return;
        input.value = value || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    return (
        <>
            {/* STATE (custom dropdown) */}
            {createPortal(
                <Select
                    classNames={getGlobalSelectClass()}
                    options={stateOptions}
                    placeholder="Select State"
                    value={state ? { label: state, value: state } : null}
                    onChange={(opt) => {
                        const val = opt?.value || '';
                        setState(val);
                        if (cityInput) {
                            setCity(''); // Clear city when state is changed
                        }
                        sync(stateInput, val); // Sync the selected state
                        sync(cityInput, ''); // Clear the city field
                    }}
                    className="input"
                    classNamePrefix="react-select"
                />,
                stateMount
            )}

            {/* CITY (text input) */}
            {cityInput && cityMount && createPortal(
                <input
                    type="text"
                    className="input"
                    name="contact.city"
                    placeholder="Enter City"
                    value={city}
                    onChange={(e) => {
                        setCity(e.target.value);
                        sync(cityInput, e.target.value); // Sync city input value
                    }}
                />,
                cityMount
            )}
        </>
    );
};

const enhanceCityStateFields = (container) => {
    const stateInput = container.querySelector('input[name="contact.state"]');
    const cityInput = container.querySelector('input[name="contact.city"]');

    if (!stateInput) return;
    if (stateInput.dataset.enhanced) return;

    stateInput.dataset.enhanced = 'true';
    if (cityInput) {
        cityInput.dataset.enhanced = 'true';
    }

    const stateControl = getFormeoControl(stateInput);
    const cityControl = cityInput ? getFormeoControl(cityInput) : null;

    if (!stateControl) return;

    // Hide native inputs
    [stateInput, cityInput].filter(Boolean).forEach(input => {
        input.classList.add('hidden-native-input');
        input.setAttribute('aria-hidden', 'true');
        input.tabIndex = -1;
    });

    // Mount points INSIDE each control
    const stateMount = document.createElement('div');
    const cityMount = document.createElement('div');

    stateMount.className = 'react-select-wrapper mt-1';
    cityMount.className = 'react-select-wrapper mt-1';

    stateControl.appendChild(stateMount);
    if (cityControl) {
        cityControl.appendChild(cityMount);
    }

    // ONE shared React root
    const rootContainer = document.createElement('div');
    const root = createRoot(rootContainer);
    stateControl.appendChild(rootContainer);

    root.render(
        <CityStatePortaledSelect
            stateInput={stateInput}
            cityInput={cityInput}
            stateMount={stateMount}
            cityMount={cityMount}
        />
    );
};



/**
 * Embedded, tells the page around the form how tall it is, so the embed
 * snippet can size the iframe to fit: nothing cut off, no empty space under
 * the confirmation (OL-224). `scroll` asks the page to bring the form into
 * view, which the snippet does only if it is off screen.
 */
const EmbedSizer = ({ embedded, scroll = false, children }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (!embedded || !ref.current) return undefined;

        const post = () => {
            const bottom = ref.current.getBoundingClientRect().bottom + window.scrollY;
            window.parent.postMessage(embedHeightMessage(bottom + 8, { scroll }), '*');
        };

        post();
        const observer = new ResizeObserver(post);
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [embedded, scroll]);

    return <div ref={ref} className="w-full">{children}</div>;
};

EmbedSizer.propTypes = {
    embedded: PropTypes.bool.isRequired,
    scroll: PropTypes.bool,
    children: PropTypes.node,
};

const IntakeFormFill = () => {
    const [searchParams] = useSearchParams();
    const [embedded] = useState(isEmbedded);
    useBodyClasses(embedded ? 'intake-form-embedded' : '');
    const cardClass = embedded
        ? 'card container-fixed mx-auto !bg-transparent !border-0 !shadow-none !rounded-none'
        : 'card container-fixed mx-auto mt-6';
    const cardBodyClass = embedded ? 'card-body !px-1 !py-2' : 'card-body';
    const token = searchParams.get('token');

    const formContainerRef = useRef(null);
    const rendererRef = useRef(null);

    const [form, setForm] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | success | error
    const [submitting, setSubmitting] = useState(false);
    const [requiredFields, setRequiredFields] = useState([]);
    const [fieldLabelMap, setFieldLabelMap] = useState({});

    // Fetch Form
    useEffect(() => {
        if (!token) {
            setStatus('error');
            return;
        }

        const fetchForm = async () => {
            try {
                setStatus('loading');
                const { data, status } = await intakeFormService.verifyIntakeForm(token);
                if (!status) throw new Error('Invalid form');
                setForm(data);
                setStatus('ready');
            } catch (err) {
                console.error(err);
                setStatus('error');
            }
        };

        fetchForm();
    }, [token]);


    // Render Formeo
    useEffect(() => {
        if (!form || !form.fields || !formContainerRef.current) return;
        if (rendererRef.current) return;

        try {
            const schema =
                typeof form.fields === 'string'
                    ? JSON.parse(form.fields)
                    : form.fields;

            stripFieldsFromSchema(schema, HIDDEN_RENDER_FIELDS);

            const fields = Array.isArray(schema.fields) ? schema.fields : Object.values(schema.fields);

            // Disable the specific select field
            const selectField = fields.find(field => field?.attrs?.name === "contact.contact_type_id");
            if (selectField) {
                selectField.attrs.disabled = true;
            }

            // Render the form using FormeoRenderer
            const renderer = new FormeoRenderer({
                renderContainer: formContainerRef.current,
            });

            renderer.render(schema);
            rendererRef.current = renderer;
            const labelMap = buildFieldLabelMapFromSchema(schema);
            setFieldLabelMap(labelMap);
            requestAnimationFrame(() => {
                attachFieldLabelsToInputs(formContainerRef.current, labelMap)
                applyInputKinds(formContainerRef.current, getInputKinds(schema));
                enhanceSelectPlaceholders(formContainerRef.current, { selector: 'select:not([multiple])' });
                enhanceMultiSelects(formContainerRef.current);
                enhanceCityStateFields(formContainerRef.current);
                injectCheckboxOtherOptions(formContainerRef.current, schema);
                enhanceRenderedSingleCheckboxPreview(formContainerRef.current);
                applyCheckboxRequiredAttributes(formContainerRef.current, schema);
                // your file input note logic (keep as-is)
                const fileInputFields = formContainerRef.current.querySelectorAll('input[type="file"]');
                fileInputFields.forEach((fileInputField) => {
                    fileInputField.setAttribute('accept', ACCEPT_FILE_TYPES);
                    if (fileInputField.dataset.fileEnhanced === 'true') return;
                    fileInputField.dataset.fileEnhanced = 'true';

                    const message = document.createElement('span');
                    message.textContent = "Allowed file types: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG, CSV. Maximum file size for each file is 100MB.";
                    message.style.color = 'text-gray-300';
                    message.style.fontSize = '12px';

                    const list = document.createElement('div');
                    list.className = 'flex items-center flex-wrap text-xs text-gray-500 mt-1 gap-3 max-h-[250px] overflow-y-auto pr-2';
                    list.dataset.fileListFor = fileInputField.name || '';

                    fileInputField.insertAdjacentElement('afterend', list);
                    list.insertAdjacentElement('afterend', message);

                    const updateList = () => renderFileList(list, fileInputField);

                    list.addEventListener('click', (e) => {
                        const removeAllBtn = e.target.closest('button[data-remove-all]');
                        if (removeAllBtn) {
                            setInputFiles(fileInputField, []);
                            updateList();
                            return;
                        }
                        const btn = e.target.closest('button[data-remove-index]');
                        if (!btn) return;
                        const idx = Number(btn.dataset.removeIndex);
                        const files = getInputFiles(fileInputField);
                        if (Number.isNaN(idx) || idx < 0 || idx >= files.length) return;
                        const next = files.filter((_, i) => i !== idx);
                        setInputFiles(fileInputField, next);
                        updateList();
                    });

                    fileInputField.addEventListener('change', () => {
                        const files = Array.from(fileInputField.files || []);
                        if (!files.length) {
                            setInputFiles(fileInputField, []);
                            updateList();
                            return;
                        }

                        const invalid = files.filter((f) => !isAllowedFileType(f));
                        if (invalid.length) {
                            toast.error(`Invalid file type: ${invalid[0].name}`);
                            fileInputField.value = '';
                            setInputFiles(fileInputField, []);
                            updateList();
                            return;
                        }

                        const tooLarge = files.find((f) => f.size > 100 * 1024 * 1024);
                        if (tooLarge) {
                            toast.error(`File "${tooLarge.name}" exceeds the 100 MB size limit.`);
                            fileInputField.value = '';
                            setInputFiles(fileInputField, []);
                            updateList();
                            return;
                        }

                        setInputFiles(fileInputField, files);
                        updateList();
                    });
                });
            });

            setRequiredFields(getRequiredFieldsFromSchema(schema));
        } catch (err) {
            console.error('Form render error', err);
            setStatus('error');
        }
    }, [form]);

    const handleSubmit = async () => {
        if (!rendererRef.current || submitting) return;

        try {
            const { missing, errors, problems, fd } = buildIntakeSubmission({
                container: formContainerRef.current,
                schema: form.fields,
                intakeFormId: form.id,
                requiredFields,
                fieldLabelMap,
            });

            showFieldProblems(formContainerRef.current, problems);

            if (missing.length) {
                toast.warning('Please fill all required fields.', {
                    description: missing.join(', '),
                });
                return;
            }

            if (errors.length) {
                errors.forEach((message) => toast.error(message));
                return;
            }

            setSubmitting(true);

            const { status } = await intakeFormService.postIntakeForm(fd, token);
            if (!status) throw new Error();

            setStatus('success');
        } catch (err) {
            console.error(err);
            toast.error('Submission failed', {
                description: 'Please try again later.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // UI States. Each returns an EmbedSizer first, so it stays mounted (and
    // keeps reporting the height) as the form moves from one state to the next.
    if (status === 'loading') {
        return (
            <EmbedSizer embedded={embedded}>
                {/* <PublicHeader showLangDropdown={false} /> */}
                <div className={`${cardClass} !p-0`}>
                    {/* <div className="card-header">
                        <h3 className="card-title">
                            <Skeleton className="h-5 w-52 dark:bg-gray-200 bg-gray-300 rounded-lg" />
                        </h3>
                    </div> */}
                    <div className={`${cardBodyClass} space-y-4`}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((section) => (
                            <div key={section}>
                                <Skeleton className="h-3.5 w-32 dark:bg-gray-200 bg-gray-300 rounded-lg" />
                                <Skeleton className="h-10 w-full dark:bg-gray-200 bg-gray-300 rounded-lg mt-2" />
                            </div>
                        ))}
                    </div>
                </div>
            </EmbedSizer>
        )
    }

    // Embedded, a message takes the form's place at the top of the frame. On
    // its own page it stays centred. Neither height may follow the viewport
    // when embedded, or the frame and the page would keep resizing each other.
    const messageWrapClass = embedded ? 'py-2' : 'flex items-center justify-center h-[80vh]';
    const messageCardClass = `card container-fixed max-w-3xl mx-auto w-full text-center border-2 border-dashed p-5 min-h-[120px] flex items-center justify-center flex-col text-sm rounded-md transition ${embedded ? '' : 'mt-6 my-5'}`;

    if (status === 'error') {
        return (
            <EmbedSizer embedded={embedded}>
                {/* <PublicHeader showLangDropdown={false} /> */}
                <div className={messageWrapClass}>
                    <div className={`${messageCardClass} border-danger bg-danger-light`}>
                        <p className="text-xl font-medium text-danger-active">Invalid or expired form</p>
                        <p className="mt-1 text-xs">This error occurs when the form link has expired or the form is no longer available in the system.</p>
                    </div>
                </div>
            </EmbedSizer>
        );
    }

    if (status === 'success') {
        return (
            <EmbedSizer embedded={embedded} scroll>
                {/* <PublicHeader showLangDropdown={false} /> */}
                <div className={messageWrapClass}>
                    <div className={`${messageCardClass} border-success bg-success-light`}>
                        {form?.confirmation_message ? (
                            // The firm's own message for this form (OL-225).
                            <p className="text-base font-medium text-success-active whitespace-pre-line">{form.confirmation_message}</p>
                        ) : (
                            <>
                                <p className="text-xl font-medium text-success-active">Form submitted successfully</p>
                                {!embedded && <p className="mt-1 text-xs">You may now close this window.</p>}
                            </>
                        )}
                    </div>
                </div>
            </EmbedSizer>
        );
    }

    return (
        <EmbedSizer embedded={embedded}>
            {/* <PublicHeader showLangDropdown={false} /> */}
            <div className={`${cardClass} p-0`}>
                {/* <div className="card-header">
                    <h3 className="card-title">{form?.name}</h3>
                </div> */}

                <div className={cardBodyClass}>
                    <div ref={formContainerRef} className="formeo-renderer-container" />

                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="btn btn-primary"
                        >
                            {submitting ? 'Submitting…' : form?.submit_button_label || 'Submit Form'}
                        </button>
                    </div>
                </div>
            </div>
        </EmbedSizer>
    );
};

export { IntakeFormFill };
